import { FC, useState, type CSSProperties } from 'react';
import { Icon, Tooltip } from '@pypestream/design-system';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import { getNodeTypeBadgeModifier } from '../../components/table/expanded-cell-view/node-type-badge';
import { agentPaletteFor, readableAgentColor } from '../../utils/agent-palette';
import type { AgentSource } from '../../stores/table-store';
import {
  UNTITLED_GROUP_LABEL,
  describeAgentSource,
  type CanvasAgentCategory,
  type CanvasAgentNodeMember,
} from './canvas-view-model';

import './agent-node-card.scss';

/**
 * Where Create got the agent: a template mark, or AI's sparkle (pulsing
 * while it drafts). Amber when something is still to connect. The words are
 * the tooltip (`describeAgentSource`).
 */
const AgentSourceBadge: FC<{ source: AgentSource }> = ({ source }) => {
  const { label, todo } = describeAgentSource(source);
  const classes = [
    'c-canvas-agent-node__source',
    `c-canvas-agent-node__source--${source.kind}`,
    source.status === 'drafting' && 'is-drafting',
    source.status === 'failed' && 'is-failed',
    !!todo && source.status !== 'failed' && 'has-todo',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <Tooltip placement="top" content={todo ? `${label}. ${todo}` : label}>
      <span
        className={classes}
        data-testid="bobcat-canvas-agent-source"
        aria-label={todo ? `${label}. ${todo}` : label}
      >
        <Icon
          name={source.kind === 'ai' ? 'stars' : 'microagent'}
          size="xsmall"
        />
      </span>
    </Tooltip>
  );
};

export interface AgentNodeData extends Record<string, unknown> {
  title: string;
  color: string | null;
  nodeCount: number;
  /** Core agents (Supervisor/Knowledge) draw heavier and taller. */
  category?: CanvasAgentCategory;
  /** Phase 2: this card's flow edges (only) are visible while it's selected. */
  isSelected?: boolean;
  /**
   * Phase 3 build A/B. `undefined`/empty when the node-detail-panel flag is
   * off, or the card is collapsed — the card then renders its header only.
   */
  members?: CanvasAgentNodeMember[];
  /**
   * Every member's node type, open or collapsed — drives the thin type-mix
   * strip under the header. Only set when node types are shown at all.
   */
  memberTypes?: CanvasAgentNodeMember['nodeType'][];
  /** Phase 3 build A: opens `NodePropertyPanel` for this member row. */
  onMemberClick?: (rowId: string) => void;
  /**
   * Phase 3 build B: color each member chip by node type (and draw the
   * strip). Off, chips render neutral — see the two flags' doc comments in
   * `bobcat-flags.ts`.
   */
  showNodeTypeDots?: boolean;
  /** Phase 3 build B: the sidebar filter's de-emphasis (0.35). */
  isDimmed?: boolean;
  /** Phase 3 build C: not connected to the selected agent (0.62). */
  isRoutingDimmed?: boolean;
  /** A Use-Case Agent no intent or flow reaches: dashed, with a tooltip. */
  isUnrouted?: boolean;
  /** Where Create got the agent from (`bobcat-agent-templates`). */
  source?: AgentSource;
  /** An edge label is being hovered and this card is not one of its ends (0.16). */
  isTraceDimmed?: boolean;
  /** An edge label is being hovered and this card IS one of its ends (1). */
  isTraceHot?: boolean;
  /** A Resource Library item is being dragged: every card can take it. */
  isDropTarget?: boolean;
  /** The card the dragged item is over. */
  isDropOver?: boolean;
  /** Member rows just placed from the Resource Library, ringed briefly. */
  freshRowIds?: Set<string>;
  /**
   * Canvas editing. Each is set only for someone who can edit; unset, the
   * card renders exactly as a read-only card did before.
   */
  onAddNode?: () => void;
  /** Unset for Supervisor and Knowledge Agent — the solution needs them. */
  onDeleteAgent?: () => void;
  onDeleteNode?: (rowId: string) => void;
  /**
   * Which end of a new connection this card offers: the Supervisor starts
   * one, a use-case agent receives it. See `canvas-view.tsx`'s `onConnect`.
   */
  connectable?: 'source' | 'target';
  /** Showing its header only (the prototype's closed group). */
  collapsed?: boolean;
  /** Open or close the card — its header caret. */
  onToggleCollapse?: () => void;
  /** "Explain this agent": what it does, how it's reached, what to check. */
  onExplain?: () => void;
}

/** Card geometry, shared with `canvas-view.tsx`'s layout and edge ports. */
export const CARD = {
  // Room for a use-case agent's whole name ("Policy coverage questions")
  // beside its source mark and node count.
  W: 236,
  W_CORE: 300,
  HEAD: 32,
  HEAD_CORE: 40,
  STRIP: 6,
  ROW: 19,
  NODES_PAD_TOP: 6,
  NODES_PAD_BOTTOM: 8,
  BORDER: 1,
  BORDER_CORE: 2,
} as const;

const isCoreCategory = (category?: CanvasAgentCategory) => category === 'core';

export const cardWidth = (category?: CanvasAgentCategory): number =>
  isCoreCategory(category) ? CARD.W_CORE : CARD.W;

const headHeight = (category?: CanvasAgentCategory): number =>
  isCoreCategory(category) ? CARD.HEAD_CORE : CARD.HEAD;

const borderWidth = (category?: CanvasAgentCategory): number =>
  isCoreCategory(category) ? CARD.BORDER_CORE : CARD.BORDER;

/** Outer height of a card, borders included. */
export const cardHeight = (
  category: CanvasAgentCategory | undefined,
  openRows: number,
  hasStrip: boolean
): number =>
  borderWidth(category) * 2 +
  headHeight(category) +
  (hasStrip ? CARD.STRIP : 0) +
  (openRows > 0
    ? CARD.NODES_PAD_TOP + openRows * CARD.ROW - 2 + CARD.NODES_PAD_BOTTOM
    : 0);

/** Y offset (from the card's top edge) of member row `i`'s centre. */
export const cardRowCenterY = (
  category: CanvasAgentCategory | undefined,
  i: number,
  hasStrip: boolean
): number =>
  borderWidth(category) +
  headHeight(category) +
  (hasStrip ? CARD.STRIP : 0) +
  CARD.NODES_PAD_TOP +
  i * CARD.ROW +
  8.5;

/**
 * Re-exported from the shared palette so existing imports keep working; the
 * darkening rule itself lives in `utils/agent-palette.ts`.
 */
export const headerColorFor = readableAgentColor;

/**
 * The one place React Flow's node shape meets the design system — Gary's
 * call in the 2026-09-22 status-check meeting (see the Build-Stage Canvas
 * PR spec). Visually it is the prototype's `.cv-group`
 * (`prostudio-build-flow-layouts.html`): a solid header in the agent's color,
 * a border tinted with it, a node-type strip, and one small chip per node.
 * React Flow sizes the node (width/height set by `canvas-view.tsx` from the
 * helpers above); the card fills it.
 */
export const AgentNodeCard: FC<NodeProps> = ({ data }) => {
  const {
    title,
    color,
    nodeCount,
    category,
    isSelected,
    members,
    memberTypes,
    onMemberClick,
    showNodeTypeDots,
    isDimmed,
    isRoutingDimmed,
    isUnrouted,
    source,
    isTraceDimmed,
    isTraceHot,
    isDropTarget,
    isDropOver,
    freshRowIds,
    onAddNode,
    collapsed,
    onToggleCollapse,
    onExplain,
    onDeleteAgent,
    onDeleteNode,
    connectable,
  } = data as unknown as AgentNodeData;
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const agentColor = headerColorFor(color);
  // Soft colors (Build polish): a Use-case agent's header is a tint of its
  // color with a solid edge. The vars are always set; the soft treatment only
  // applies under `.bobcat-polish` (five-stage nav on), see the SCSS.
  const palette = agentPaletteFor({
    color,
    category: category ?? 'use-case',
  });

  const classNames = [
    'c-canvas-agent-node',
    isCoreCategory(category) && 'c-canvas-agent-node--core',
    palette.soft && agentColor && 'c-canvas-agent-node--soft',
    isSelected && 'c-canvas-agent-node--selected',
    isRoutingDimmed && 'c-canvas-agent-node--routing-dimmed',
    isUnrouted && 'c-canvas-agent-node--unrouted',
    isDimmed && 'c-canvas-agent-node--dimmed',
    isTraceDimmed && 'c-canvas-agent-node--trace-dimmed',
    isTraceHot && 'c-canvas-agent-node--trace-hot',
    isDropTarget && 'c-canvas-agent-node--drop-target',
    isDropOver && 'c-canvas-agent-node--drop-over',
    connectable && `c-canvas-agent-node--connect-${connectable}`,
  ]
    .filter(Boolean)
    .join(' ');

  const typeCounts = (memberTypes ?? []).reduce<Record<string, number>>(
    (acc, type) => {
      const key = getNodeTypeBadgeModifier(type);
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    },
    {}
  );
  const showStrip = !!showNodeTypeDots && (memberTypes?.length ?? 0) > 0;

  return (
    <div
      className={classNames}
      style={
        agentColor
          ? ({
              '--agent': agentColor,
              ...(palette.soft
                ? {
                    '--agent-soft-fill': palette.fill,
                    '--agent-soft-text': palette.text,
                    '--agent-soft-body': palette.bodyTint,
                  }
                : {}),
            } as CSSProperties)
          : undefined
      }
      title={
        isUnrouted
          ? 'Not routed: no intent or other agent leads here yet'
          : undefined
      }
    >
      {/* Phase 2: edges connect through these. Routed edges draw their own
          geometry (`routed-edge.tsx`), but React Flow still needs a handle on
          each end to accept the edge at all. */}
      <Handle type="target" position={Position.Left} />
      <Handle type="source" position={Position.Right} />
      <div className="c-canvas-agent-node__head">
        <span
          className="c-canvas-agent-node__name"
          title={title || UNTITLED_GROUP_LABEL}
        >
          {title || UNTITLED_GROUP_LABEL}
        </span>
        {source && <AgentSourceBadge source={source} />}
        {confirmingDelete ? (
          // Inline, not a browser confirm(): deleting an agent removes its
          // nodes too, so it asks — but in place, on the card being deleted.
          // `nodrag` + stopPropagation keep React Flow and the card's own
          // click (selection) out of it.
          <span
            className="c-canvas-agent-node__confirm nodrag"
            title={`Deletes this agent and its ${nodeCount} ${
              nodeCount === 1 ? 'node' : 'nodes'
            }`}
          >
            Delete?
            <button
              type="button"
              data-testid="bobcat-canvas-delete-agent-confirm"
              onClick={(event) => {
                event.stopPropagation();
                setConfirmingDelete(false);
                onDeleteAgent?.();
              }}
            >
              Delete
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                setConfirmingDelete(false);
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <>
            {onExplain && (
              <Tooltip placement="top" content="Explain this agent">
                <button
                  type="button"
                  className="c-canvas-agent-node__act nodrag"
                  data-testid="bobcat-canvas-explain-agent"
                  aria-label={`Explain ${title || UNTITLED_GROUP_LABEL}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onExplain();
                  }}
                >
                  <Icon name="stars" size="xsmall" />
                </button>
              </Tooltip>
            )}
            {onAddNode && (
              <Tooltip
                placement="top"
                content={`Add a node to ${title || UNTITLED_GROUP_LABEL}`}
              >
                <button
                  type="button"
                  className="c-canvas-agent-node__act nodrag"
                  data-testid="bobcat-canvas-add-node"
                  aria-label={`Add a node to ${title || UNTITLED_GROUP_LABEL}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddNode();
                  }}
                >
                  <Icon name="add" size="xsmall" />
                </button>
              </Tooltip>
            )}
            {onDeleteAgent && (
              <Tooltip
                placement="top"
                content={`Delete ${title || UNTITLED_GROUP_LABEL} and its nodes`}
              >
                <button
                  type="button"
                  className="c-canvas-agent-node__act nodrag"
                  data-testid="bobcat-canvas-delete-agent"
                  aria-label={`Delete ${title || UNTITLED_GROUP_LABEL}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    setConfirmingDelete(true);
                  }}
                >
                  <Icon name="delete" size="xsmall" />
                </button>
              </Tooltip>
            )}
            <span className="c-canvas-agent-node__count">{nodeCount}</span>
            {onToggleCollapse && (
              <Tooltip
                placement="top"
                content={collapsed ? 'Expand' : 'Collapse'}
              >
                <button
                  type="button"
                  className="c-canvas-agent-node__act nodrag"
                  data-testid="bobcat-canvas-agent-collapse"
                  aria-expanded={!collapsed}
                  aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${
                    title || UNTITLED_GROUP_LABEL
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleCollapse();
                  }}
                >
                  <Icon
                    name={collapsed ? 'expand' : 'collapse'}
                    size="xsmall"
                  />
                </button>
              </Tooltip>
            )}
          </>
        )}
      </div>
      {showStrip && (
        <div className="c-canvas-agent-node__strip" aria-hidden="true">
          {Object.entries(typeCounts).map(([type, count]) => (
            <span
              key={type}
              className={`c-canvas-agent-node__strip-seg c-canvas-agent-node__strip-seg--${type}`}
              style={{ flex: count }}
            />
          ))}
        </div>
      )}
      {members && members.length > 0 && (
        <ul className="c-canvas-agent-node__members">
          {members.map((member) => (
            <li key={member.id}>
              {/* `stopPropagation` so clicking a chip does not also toggle
                  this card's own selection (canvas-view.tsx's `onNodeClick`),
                  which reads from the same DOM click. */}
              <button
                type="button"
                className={`c-canvas-agent-node__member c-canvas-agent-node__member--${
                  showNodeTypeDots
                    ? getNodeTypeBadgeModifier(member.nodeType)
                    : 'plain'
                }${freshRowIds?.has(member.id) ? ' is-fresh' : ''}`}
                data-testid="bobcat-canvas-node-member"
                title={`${member.nodeNumber ?? '—'} · ${member.label}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onMemberClick?.(member.id);
                }}
              >
                {member.label}
                <span className="c-canvas-agent-node__member-num">
                  {member.nodeNumber ?? '—'}
                </span>
              </button>
              {onDeleteNode && (
                <button
                  type="button"
                  className="c-canvas-agent-node__member-del nodrag"
                  data-testid="bobcat-canvas-delete-node"
                  aria-label={`Delete node ${member.nodeNumber ?? ''} ${member.label}`}
                  title="Delete node"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteNode(member.id);
                  }}
                >
                  <Icon name="close" size="xsmall" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
